import React from "react";
import {
  LayoutDashboard,
  Users,
  PhoneCall,
  Calendar,
  Settings,
  BarChart3,
  LogOut,
} from "lucide-react";

export default function Dashboard() {
  return (
    <div className="min-h-screen flex bg-gray-50 text-gray-800">

      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="px-6 py-5 border-b border-gray-200">
          <h1 className="text-xl font-semibold text-blue-700 flex items-center gap-2">
            <BarChart3 className="text-blue-600" size={22} />
            CallAI CRM
          </h1>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-2">
          <SidebarLink icon={<LayoutDashboard size={18} />} label="Dashboard" active />
          <SidebarLink icon={<Users size={18} />} label="Leads" />
          <SidebarLink icon={<PhoneCall size={18} />} label="Campaigns" />
          <SidebarLink icon={<Calendar size={18} />} label="Appointments" />
          <SidebarLink icon={<Settings size={18} />} label="Settings" />
        </nav>

        <div className="border-t border-gray-200 px-4 py-4">
          <button className="flex items-center gap-2 text-gray-600 hover:text-red-500 text-sm font-medium">
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">

        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm px-8 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-700">Dashboard Overview</h2>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-500">Welcome, Gurmit</div>
            <img
              src="https://ui-avatars.com/api/?name=Gurmit+Singh&background=0D8ABC&color=fff"
              alt="user avatar"
              className="w-8 h-8 rounded-full border border-gray-300"
            />
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-8 overflow-y-auto">

          {/* KPI Cards */}
          <h3 className="text-lg font-semibold text-gray-700 mb-6">Today's Stats</h3>
          <div className="grid gap-6 md:grid-cols-3">
            <StatCard title="Total Leads" value="348" change="+23 since yesterday" color="blue" />
            <StatCard title="Appointments" value="22" change="3 booked today" color="indigo" />
            <StatCard title="Active Campaigns" value="12" change="2 new this week" color="emerald" />
          </div>

          {/* Performance Section */}
          <h3 className="text-lg font-semibold text-gray-700 mt-12 mb-6">Performance Metrics</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <PerformanceCard
              title="Lead Conversion Rate"
              value="32%"
              subtitle="Up 5% this week"
              color="blue"
            />
            <PerformanceCard
              title="Avg. Call Duration"
              value="2m 43s"
              subtitle="Steady from last week"
              color="purple"
            />
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 py-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} CallAI CRM — Automated Cold Calling Platform
        </footer>
      </div>
    </div>
  );
}

/* ---- Subcomponents ---- */

function SidebarLink({ icon, label, active }: { icon: React.ReactNode; label: string; active?: boolean }) {
  return (
    <button
      className={`flex items-center w-full gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "bg-blue-50 text-blue-700"
          : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function StatCard({
  title,
  value,
  change,
  color,
}: {
  title: string;
  value: string;
  change: string;
  color: "blue" | "indigo" | "emerald";
}) {
  const colorMap: Record<string, string> = {
    blue: "text-blue-600",
    indigo: "text-indigo-600",
    emerald: "text-emerald-600",
  };
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <p className="text-sm text-gray-500">{title}</p>
      <p className={`text-3xl font-bold mt-2 ${colorMap[color]}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{change}</p>
    </div>
  );
}

function PerformanceCard({
  title,
  value,
  subtitle,
  color,
}: {
  title: string;
  value: string;
  subtitle: string;
  color: "blue" | "purple";
}) {
  const colorMap: Record<string, string> = {
    blue: "text-blue-600",
    purple: "text-purple-600",
  };
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h4 className="font-medium text-gray-700 mb-2">{title}</h4>
      <div className={`text-3xl font-bold ${colorMap[color]}`}>{value}</div>
      <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
    </div>
  );
}
